/* eslint-disable no-undef */
import { Box, Center, Divider, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';

import ButtonedRadioGroup from '../../../components/ButtonedRadioGroup';
import EmployerNameAndPhoneInputs from '../../../components/EmployerNameAndPhoneInputs';
import { useLEAccordionItemContext } from '../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import TextWithTooltip from '../../../components/TextWithTooltip';
import TimeAtEmployment from '../../../components/TimeAtEmployment';
import { Label } from '../../../components/formComponents';
import Input from '../../../components/formComponents/Input';
import NumberInput from '../../../components/formComponents/NumberInput';
import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import {
  EmployerPhoneLookupForm,
  EmploymentStatusEnum,
  useTemporaryInfoUpdateMutation,
} from '../../../gql/generated/graphql';
import { useTempInfo } from '../../../hooks';
import { useLookupRegistry } from '../../../hooks/useLookupRegistry';
import { capitalizeFirstLetter } from '../../../utils/strings';
import { BaseEmploymentFormValues } from '../../LoanApp/utils';
import {
  CobuyerEmploymentFormValues,
  cobuyerEmploymentInitialValues,
  cobuyerEmploymentValidationSchema,
} from '../utils';

const CobuyerEmployment = () => {
  const { info } = useTempInfo();
  const tempInfoData = info?.data;

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();

  const { updateState, index, setOpenAccordionIndex } = useLEAccordionItemContext();
  const { registerLookup, finalizeLookups } = useLookupRegistry();

  const onSubmitForm = async (values: CobuyerEmploymentFormValues) => {
    updateState({ status: 'loading' });

    const data = {
      cobuyer_employment_status: values.cobuyer_employment_status,
      cobuyer_employer_name: values.cobuyer_employer_name.slice(0, 25).trim(),
      cobuyer_employer_phone_number: values.cobuyer_employer_phone_number,
      cobuyer_job_title: values.cobuyer_job_title.slice(0, 20).trim(),
      cobuyer_years_at_job: values.cobuyer_years_at_job,
      cobuyer_months_at_job: values.cobuyer_months_at_job ?? 0,
      cobuyer_salary: values.cobuyer_salary,
      cobuyer_pay_frequency: values.cobuyer_pay_frequency,
      cobuyer_previous_employer_name: values.cobuyer_previous_employer_name.slice(0, 25).trim(),
      cobuyer_previous_employer_phone_number: values.cobuyer_previous_employer_phone_number,
      cobuyer_previous_job_title: values.cobuyer_previous_job_title.slice(0, 20).trim(),
      cobuyer_previous_years_at_job: values.cobuyer_previous_years_at_job,
      cobuyer_previous_months_at_job: values.cobuyer_previous_months_at_job ?? 0,
    };

    await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data,
        },
      },
    });

    updateState({ status: 'complete' });
    setOpenAccordionIndex(index + 1);
  };

  if (!tempInfoData) {
    return null;
  }

  return (
    <Box>
      <Center mb={{ base: 4, md: 0 }}>
        <TextWithTooltip label="To assess risk and establish a regular history of income, lenders prefer looking at more than two years' worth of data.">
          Why do we need this?
        </TextWithTooltip>
      </Center>
      <Formik<CobuyerEmploymentFormValues>
        enableReinitialize
        initialValues={cobuyerEmploymentInitialValues(tempInfoData)}
        validationSchema={cobuyerEmploymentValidationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmitForm}
      >
        {({ isSubmitting, values, setFieldValue, errors, submitForm }) => {
          const isEmployed = values?.cobuyer_employment_status === EmploymentStatusEnum.Employed;
          const hasErrors = Object.values(errors).some((value) => value !== '');

          const handleNext = async () => {
            await finalizeLookups();
            submitForm();
          };

          return (
            <Form
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'INPUT') {
                  event.preventDefault();
                  handleNext();
                }
              }}
            >
              <Label mt={5}>EMPLOYMENT STATUS</Label>
              <ButtonedRadioGroup
                columns={3}
                collapseOnMobile={false}
                onChange={(value) => setFieldValue('cobuyer_employment_status', value)}
                options={[
                  { label: 'EMPLOYED', value: EmploymentStatusEnum.Employed },
                  { label: 'NOT EMPLOYED', value: EmploymentStatusEnum.NotEmployed },
                  { label: 'RETIRED', value: EmploymentStatusEnum.Retired },
                ]}
                name="cobuyer_employment_status"
                value={values?.cobuyer_employment_status}
                defaultValue={EmploymentStatusEnum.Employed}
                mb={8}
              />
              {isEmployed && (
                <>
                  <EmployerNameAndPhoneInputs
                    form={EmployerPhoneLookupForm.CobuyerCurrent}
                    nameField="cobuyer_employer_name"
                    phoneField="cobuyer_employer_phone_number"
                    mb={4}
                    registerLookup={registerLookup}
                  />
                  <Input label="JOB TITLE" name="cobuyer_job_title" mb={4} maxLength={50} />
                </>
              )}
              <NumberInput
                name="cobuyer_salary"
                isMoney
                type="decimal"
                _input={{ inputMode: 'numeric' }}
                label="ANNUAL GROSS INCOME"
                mb={2}
              />
              <TextWithTooltip label="Include all sources of income such as employment earnings, self-employment income, investments, retirement funds, etc. This information should reflect only the co-buyer's income—do not include household or combined income.">
                Multiple sources of income?
              </TextWithTooltip>
              <Divider my="20px" />
              <TimeAtEmployment
                values={values as unknown as BaseEmploymentFormValues}
                isCobuyer
                firstName={capitalizeFirstLetter(tempInfoData?.cobuyer_first_name)}
                registerLookup={registerLookup}
              />
              <Center>
                <PrimaryButton
                  mt="30px"
                  mb={{
                    base: '20px',
                    lg: '0',
                  }}
                  loading={isSubmitting}
                  type="button"
                  onClick={handleNext}
                />
              </Center>
              {hasErrors && (
                <Text color="leaseEndRed" textAlign="center" mt={{ base: 0, lg: 4 }}>
                  Review fields above for errors.
                </Text>
              )}
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CobuyerEmployment;
